/**
 * Fraud Alerts Page
 * Phase 4 — chargeback detection surface. Admin/super-admin only.
 *
 * Newest-first list of `db.admin_alerts` where type='chargeback'.
 * Each row shows the user's @handle + name, chargeback amount, reason,
 * and Unfreeze / Ban actions. The Ban action reuses the existing
 * User Records suspend/ban path (link out).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAdmin } from '../AdminApp';
import { toast } from 'sonner';
import {
  Search,
  ChevronLeft,
  ChevronRight,
  ShieldAlert,
  Unlock,
  Ban,
  ExternalLink,
  CheckCircle,
} from 'lucide-react';

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const displayHandle = (h) => (h ? (h.startsWith('@') ? h : `@${h}`) : '—');

const FraudAlertsPage = () => {
  const { api } = useAdmin();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [pendingQ, setPendingQ] = useState('');
  const [resolvedFilter, setResolvedFilter] = useState('unresolved');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [unresolvedCount, setUnresolvedCount] = useState(0);
  const perPage = 20;
  const totalPages = Math.max(1, Math.ceil(total / perPage));

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: perPage, resolved: resolvedFilter };
      if (q) params.q = q;
      const res = await api.get('/api/admin/fraud-alerts', { params });
      setAlerts(res.data?.alerts || []);
      setTotal(res.data?.total || 0);
      setUnresolvedCount(res.data?.unresolved_count || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load fraud alerts');
    } finally {
      setLoading(false);
    }
  }, [api, page, perPage, resolvedFilter, q]);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const submitSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setQ(pendingQ.trim());
  };

  const unfreezeUser = async (userId, handle) => {
    if (!window.confirm(`Unfreeze ${displayHandle(handle) || userId}'s wallet? This resolves all open chargeback alerts.`)) {
      return;
    }
    try {
      const res = await api.post(`/api/admin/users/${userId}/unfreeze`);
      toast.success(`Wallet unfrozen — ${res.data?.alerts_resolved || 0} alert(s) resolved`);
      fetchAlerts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Unfreeze failed');
    }
  };

  return (
    <div className="p-6 text-white" data-testid="admin-fraud-alerts-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <ShieldAlert className="text-red-400" size={28} />
          <div>
            <h1 className="text-2xl font-bold">Fraud Alerts</h1>
            <p className="text-sm text-gray-400">
              Chargebacks & wallet freezes. Newest first.
            </p>
          </div>
        </div>
        {unresolvedCount > 0 && (
          <div
            className="px-3 py-1 rounded-full bg-red-500/20 border border-red-500/50 text-red-200 text-sm font-medium"
            data-testid="fraud-alerts-unresolved-badge"
          >
            {unresolvedCount} unresolved
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <form onSubmit={submitSearch} className="flex-1 min-w-[260px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              value={pendingQ}
              onChange={(e) => setPendingQ(e.target.value)}
              placeholder="Search by @handle, name, user id, or chargeback id…"
              className="w-full pl-9 pr-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-white focus:outline-none focus:border-red-500"
              data-testid="fraud-alerts-search-input"
            />
          </div>
        </form>
        <select
          value={resolvedFilter}
          onChange={(e) => { setPage(1); setResolvedFilter(e.target.value); }}
          className="px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-white"
          data-testid="fraud-alerts-status-filter"
        >
          <option value="unresolved">Unresolved</option>
          <option value="resolved">Resolved</option>
          <option value="all">All</option>
        </select>
      </div>

      <div className="rounded-lg border border-gray-700 overflow-hidden bg-gray-900">
        {loading ? (
          <div className="p-6 text-center text-gray-400">Loading…</div>
        ) : alerts.length === 0 ? (
          <div className="p-6 text-center text-gray-400" data-testid="fraud-alerts-empty">
            No fraud alerts.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-800 text-gray-400 text-left">
              <tr>
                <th className="p-3">When</th>
                <th className="p-3">User</th>
                <th className="p-3">Amount</th>
                <th className="p-3">Reason</th>
                <th className="p-3">Source</th>
                <th className="p-3">Status</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {alerts.map((a) => (
                <tr
                  key={a.id}
                  className="border-t border-gray-800 hover:bg-gray-800/60"
                  data-testid={`fraud-alert-row-${a.id}`}
                >
                  <td className="p-3 text-gray-300 whitespace-nowrap">{formatDate(a.created_at)}</td>
                  <td className="p-3">
                    <div className="font-medium">{a.user_name || '—'}</div>
                    <div className="text-xs text-gray-400" data-testid={`fraud-alert-handle-${a.id}`}>
                      {displayHandle(a.user_handle)}
                    </div>
                  </td>
                  <td className="p-3 whitespace-nowrap">
                    <div className="font-semibold text-red-300">
                      ${Number(a.amount_usd || 0).toFixed(2)}
                    </div>
                    <div className="text-xs text-gray-400">
                      clawed back {Number(a.emeralds_clawed_back || 0).toLocaleString()} 🟢
                      {a.shortfall_emeralds ? (
                        <> · short {Number(a.shortfall_emeralds).toLocaleString()}</>
                      ) : null}
                    </div>
                  </td>
                  <td className="p-3 text-gray-300">{a.reason || '—'}</td>
                  <td className="p-3">
                    <span className="inline-block px-2 py-0.5 rounded text-[11px] bg-gray-700 text-gray-200 border border-gray-600 uppercase">
                      {(a.chargeback_id ? 'chargeback' : 'alert')}
                    </span>
                  </td>
                  <td className="p-3">
                    {a.resolved ? (
                      <span className="inline-flex items-center gap-1 text-green-400 text-xs">
                        <CheckCircle size={14} /> resolved
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-amber-400 text-xs">
                        <ShieldAlert size={14} /> open
                      </span>
                    )}
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    {!a.resolved && (
                      <button
                        onClick={() => unfreezeUser(a.user_id, a.user_handle)}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-emerald-600 hover:bg-emerald-500 text-white mr-2"
                        data-testid={`fraud-alert-unfreeze-btn-${a.id}`}
                      >
                        <Unlock size={12} /> Unfreeze
                      </button>
                    )}
                    <a
                      href={`/admin/users?q=${encodeURIComponent(a.user_handle || a.user_id || '')}`}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-gray-700 hover:bg-gray-600 text-white"
                      data-testid={`fraud-alert-open-user-${a.id}`}
                      title="Open in User Records to Ban / view history"
                    >
                      <Ban size={12} /> User Records <ExternalLink size={10} />
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      <div className="flex justify-between items-center mt-4 text-sm text-gray-400">
        <div>
          {total > 0 ? (
            <>Showing {(page - 1) * perPage + 1}–{Math.min(page * perPage, total)} of {total}</>
          ) : null}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="px-2 py-1 rounded bg-gray-800 disabled:opacity-40"
          >
            <ChevronLeft size={14} />
          </button>
          <span className="px-2 py-1">{page} / {totalPages}</span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="px-2 py-1 rounded bg-gray-800 disabled:opacity-40"
          >
            <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default FraudAlertsPage;
